/**
 * Per-action execution — steps 2–3 of the turn pipeline for each v0 verb
 * (architecture.md §7.2), carrying the execution semantics owed from
 * R1.1–R4.
 *
 * @remarks
 * Every handler is pure: bundle + state + action + cursor in, either a
 * completed step (next state, emitted entries, advanced cursor) or a typed
 * rejection out. Handlers never append to `session.transcript` and never
 * touch `turnCount` — `executeTurn` owns commitment and time.
 *
 * The owed semantics, and where they live:
 *
 * - **Lock refusals** (`handleGo`): TRUE means LOCKED; `unlockedExitIds`
 *   (fed by `unlock_exit`) wins over the bolt; a lock without a `lockedWhen` is
 *   locked until something unlocks it; D233's `blockedWhen` — the obstruction,
 *   a body in the way — holds whatever the ledger says. Which refusal speaks is the lock's
 *   own conditional answer (D158) — "you are carrying the answer" is one
 *   `has_item` variant of it, not a field of its own.
 * - **Outward exits never traverse** (D19a, `handleGo`): checked BEFORE the
 *   lock, so they fail safe regardless of lock state; authored lock
 *   narratives speak if present, the generic default otherwise.
 * - **`take` channels** (`handleTake`): `takeNarratives` or the generic
 *   acknowledgement; fixtures and ambient items refuse with design doc
 *   §6.3's generic line (per-item SUCCESS beats are authored; per-item
 *   refusals are the typed rejection + the generic line — R4 handoff).
 * - **`inspect` channels** (`handleInspect`): `description` speaks first on
 *   `environment`, then `inspectNarratives` — ABSENCE IS SILENCE.
 * - **`ask` channels** (`handleAsk`, in `ask-handler.ts` since D231): a
 *   topic, the subject's `askNarratives`, the addressee's fallback, or the
 *   generic deflection; the handwritten log's missing entry MUST keep
 *   falling through to the deflection (design doc §3.3 — do not "fix" it).
 * - **Narrative-only interactions** (`handleUse`): empty `effects` with
 *   min-1 `narratives` is authored refusal fiction and completes normally,
 *   consuming the turn.
 *
 * @packageDocumentation
 */

import { type ExitDefinition, type ExitLock, isUsedAlone, type ItemId, type NarrativeLine, type RuntimeState, type UseInteraction } from '@orpheus/content-schema';

import type { RuntimeAction } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { requireItem, requireRoom } from '../bundle/bundle-lookups.js';
import { effectiveSaveModeOf } from '../bundle/host-declaration.js';
import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { firstMatchingVariant } from '../conditions/first-matching-variant.js';
import { resolveEngineResponse, speakSlot, type SpokenResponse } from '../defaults/default-responses.js';
import { renderDirectiveListing } from '../defaults/render-directive-listing.js';
import { deriveMapGraph } from '../map/derive-map-graph.js';
import { resolveMapPresentation } from '../map/map-presentation.js';
import { renderMapText } from '../map/render-map-text.js';
import { describeItemInState, describeRoomInState, resolveConditionalText } from '../narrative/conditional-text.js';
import { type ActionHandlerResult, completed, completedSpeaking, rejected } from './action-handler-result.js';
import { CHROME_DIRECTIVE_TOKENS } from './chrome-directive-tokens.js';
import { droppedHereResponse } from './dropped-here.js';
import { EngineInvariantError } from './engine-invariant-error.js';
import { enterRoom } from './enter-room.js';
import { visibleExitsOf } from './exit-visibility.js';
import { exitAnsweringTo } from './exit-words.js';
import { stampNarrativeLines, type TranscriptCursor } from './transcript-cursor.js';
import { withEffects } from './with-effects.js';

// D53: the dispatcher and the tests import the whole handler roster from here,
// so the container split stays invisible to every caller.
export { type ActionHandlerResult, type CompletedAction } from './action-handler-result.js';
export { handleDoor, handleSearch, handleSearchRoom, handleSwitch } from './container-handlers.js';
// R25: the carry family (take, drop, the acquisition record, the capacity
// gate) moved out at the 500-line cap — the container-handlers precedent.
export { handleDrop, handleTake } from './carry-handlers.js';
// D143: the wear family — the carry family's third pair, in its own module.
export { handleEquip, handleUnequip, handleWear } from './equip-handlers.js';
// D231: the question — topics, the one-topic dossier, the fallback, the deflection — in its own module.
export { handleAsk } from './ask-handler.js';

/**
 * `go` — exit traversal with the full refusal ladder: no exit, outward exit
 * (D19a), locked exit (ruled lock semantics), then arrival via
 * {@link enterRoom}.
 */
export const handleGo = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'go' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const activeRoom = requireRoom(bundle, state.progression.activeRoomId);
	// D138: a hidden exit is a wall to every lookup below — not a locked door,
	// a wall — until the reveal watcher has written it into the ledger.
	const exits = visibleExitsOf(activeRoom.exits, state);

	// D31: a destination-only go resolves through the room's authored exit
	// links ("go to the stairwell" — the stairwell IS the way down). The full
	// refusal ladder below applies unchanged: links grant no lock bypass.
	if (action.directionToken === undefined) {
		if (action.destinationItemId === undefined) {
			throw new EngineInvariantError('go arrived with neither a direction nor a destination — narrowing guarantees one');
		}

		const destinationItemId = action.destinationItemId;
		const linkedExit = exits.find((candidate) => candidate.destinationItemIds?.includes(destinationItemId));

		if (linkedExit === undefined) {
			return rejected(bundle, state, { itemId: destinationItemId, type: 'no_path_toward' }, [
				speakSlot(bundle, state, 'noPathToThat', { itemName: requireItem(bundle, destinationItemId).name }),
			]);
		}

		return traverseExit(bundle, state, linkedExit, cursor);
	}

	// D227 (E21): an exit answers to its `directionToken` and to every word
	// it `alsoAnswersTo` — the road north is also the road up.
	const exit = exitAnsweringTo(exits, action.directionToken);

	if (exit === undefined) {
		return rejected(bundle, state, { directionToken: action.directionToken, type: 'no_exit_that_way' }, [
			speakSlot(bundle, state, 'noExitThatWay'),
		]);
	}

	return traverseExit(bundle, state, exit, cursor);
};

/**
 * The shared traversal tail of `handleGo` — outward check, lock ladder, and
 * the move itself — identical for bearing-resolved and destination-resolved
 * exits (D31: links change how an exit is FOUND, never what it permits).
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the move
 * @param exit - The resolved exit
 * @param cursor - This turn's transcript cursor
 * @returns The handler result
 */
const traverseExit = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	exit: ExitDefinition,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	// Outward exits never traverse in v0 — before the lock, so they fail safe
	// regardless of lock state (D19a).
	if (exit.leadsOutOfPack === true) {
		return rejected(bundle, state,
			{ exitId: exit.exitId, type: 'exit_leads_outward' },
			exit.lock === undefined ? [speakSlot(bundle, state, 'exitLeadsOutward')] : selectLockNarratives(exit.lock, bundle, state),
		);
	}

	const engagedLock = engagedExitLock(exit, bundle, state);

	if (engagedLock !== undefined) {
		return rejected(bundle, state,
			{ exitId: exit.exitId, type: 'exit_locked' },
			selectLockNarratives(engagedLock, bundle, state),
		);
	}

	if (exit.targetRoomId === undefined) {
		throw new EngineInvariantError(
			`Exit "${exit.exitId}" has neither a target room nor an outward declaration — the schema's XOR refinement should have rejected this`,
		);
	}

	const entry = enterRoom(bundle, state, exit.targetRoomId, cursor);

	return completed(entry.state, { cursor: entry.cursor, entries: entry.entries });
};

/**
 * `help` — the engine's verb surface, system voice.
 *
 * @remarks
 * D43: the listing is derived from the bundle's own compiled vocabulary, so
 * HELP cannot drift from what the grammar accepts. A pack that overrides the
 * `help` slot may still embed `{directives}` to re-voice the frame while
 * keeping the derived list.
 */
/**
 * D179: the directives HELP must not advertise in this game — `save` and
 * `load` on a pack that saves itself, where each would only say there is
 * nothing to do. The words stay in the vocabulary (so they answer), and the
 * listing follows the mode — D231: the EFFECTIVE mode, so a host that keeps
 * nothing withholds them too.
 *
 * @param bundle - The loaded bundle
 * @returns The canonical tokens to withhold
 */
const withheldDirectivesOf = (bundle: EngineGameBundle): readonly string[] =>
	effectiveSaveModeOf(bundle) === 'manual' ? [] : [CHROME_DIRECTIVE_TOKENS.save, CHROME_DIRECTIVE_TOKENS.load];

export const handleHelp = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
): ActionHandlerResult =>
	completedSpeaking(
		bundle,
		state,
		cursor,
		[speakSlot(bundle, state, 'help', { directives: renderDirectiveListing(bundle, withheldDirectivesOf(bundle)) })],
		'help',
	);

/** `map` (D33) — the discovered world rendered into the transcript, system voice, fog and all. */
export const handleMap = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
): ActionHandlerResult =>
	completed(
		state,
		stampNarrativeLines(
			bundle,
			state,
			cursor,
			[
				{
					priority: 'standard',
					source: 'system',
					// D189: headed with what the pack calls its map, resolved the way the loader resolves it for the shell.
					text: renderMapText(deriveMapGraph(bundle, state), resolveMapPresentation(bundle.contentPack.manifest.presentation?.map).title),
				},
			],
			'map',
		),
	);

/** `inspect` — the item's `description` first, then its `inspectNarratives` (absence is silence). */
export const handleInspect = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'inspect' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const item = requireItem(bundle, action.itemId);

	// R12: reading IS inspecting. The three-beat arc, in order: the thing
	// (`description`), the document's own voice (`log.narratives` — every
	// read rereads it), then the commentary (`inspectNarratives` — EURYDICE
	// gets to comment AFTER the read, as the items schema promised in R4).
	const lines: readonly NarrativeLine[] = [
		{ priority: 'standard', source: 'environment', text: describeItemInState(item, bundle, state) },
		...(item.log?.narratives ?? []),
		...(item.inspectNarratives ?? []),
	];

	// The FIRST read marks the log; a reread never clears the mark. `log_read`
	// triggers see the mark this same turn (step 7 runs after the handler).
	const logId = item.log?.logId;
	const readState =
		logId === undefined || state.narrative.readLogIds.includes(logId)
			? state
			: {
				...state,
				narrative: { ...state.narrative, readLogIds: [...state.narrative.readLogIds, logId]},
			};

	return completed(readState, stampNarrativeLines(bundle, state, cursor, lines, `inspect:${action.itemId}`));
};

/**
 * `wait` (D52) — time passes, and nothing else does.
 *
 * @remarks
 * The whole verb: consume the turn, speak the slot, touch no state. Cheap
 * enough that leaving it a declared-unsupported verb was never a saving, and
 * the one a player types the moment a clock is mentioned — which the slice's
 * closing beat does ("The next part of the descent has a clock on it"). Load-
 * bearing since R9 delivered the scheduler: waiting is how a player chooses
 * to let a countdown run, and Calder's noticed-behavior beats listen for it.
 */
export const handleWait = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
): ActionHandlerResult =>
	completedSpeaking(bundle, state, cursor, [speakSlot(bundle, state, 'waitAcknowledgement')], 'wait');

/**
 * `inventory` — carried item names in carry order, system voice; a worn
 * thing's name passes through `wornMarker` on the way (D143 addendum —
 * Calder's finding: the one question the inventory is asked after `wear`
 * is whether it is on, and a listing that stays quiet about it is the §2.2
 * shape, two forms disagreeing about what the world contains).
 */
export const handleInventory = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const wornItemIds = new Set(Object.values(state.world.equippedItemIds));
	const carriedNames = state.world.inventoryItemIds.map((itemId): string => {
		const itemName = requireItem(bundle, itemId).name;

		return wornItemIds.has(itemId)
			? resolveConditionalText(resolveEngineResponse(bundle, 'wornMarker', { itemName }).text, bundle, state)
			: itemName;
	});
	const spoken =
		carriedNames.length === 0
			? speakSlot(bundle, state, 'inventoryEmpty')
			: speakSlot(bundle, state, 'inventoryList', { itemNames: carriedNames.join(', ') });

	return completedSpeaking(bundle, state, cursor, [spoken], 'inventory');
};

/** `look` — the active room's repeatable description (first-visit beats never re-fire here). */
export const handleLook = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const activeRoom = requireRoom(bundle, state.progression.activeRoomId);
	// D226 (E8): what the player set down here follows the paragraph, as it
	// does on arrival (`enterRoom`) — the one sentence the pack cannot write.
	const dropped = droppedHereResponse(bundle, state, activeRoom.roomId);

	return completedSpeaking(
		bundle,
		state,
		cursor,
		// D65: rooms resolve through the ONE describer, so a room keyed on the
		// breaker flag goes dark exactly as an item would — and D140's
		// fragments append the same way here as on entry.
		[
			{ priority: 'standard', source: 'environment', text: describeRoomInState(activeRoom, 'full', bundle, state) },
			...(dropped === undefined ? [] : [dropped]),
		],
		`look:${activeRoom.roomId}`,
	);
};

/**
 * `use` — both the `use` and `open` intents land here (R2 handoff). The
 * instrument's authored interactions are searched for the target in authored
 * order; the first whose conditions ALL hold wins. Its narratives speak
 * first, then its effects apply in authored order (any effect-emitted
 * narration follows). Interactions targeting exits (`targetExitId`) cannot
 * match in v0 — exits are not referable entities yet — and are skipped by
 * construction.
 *
 * D245: a `use` with no target is the thing used on its own, and reads the
 * arms that name none (`isUsedAlone`). Narrowing chose it because one of
 * them holds, so the refusal below is this handler being total rather than a
 * road the pipeline takes.
 */
export const handleUse = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'use' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const instrument = requireItem(bundle, action.instrumentItemId);

	if (action.targetItemId === undefined) {
		return performUse(bundle, state, cursor, { instrumentItemId: action.instrumentItemId }, instrument.useInteractions.filter(isUsedAlone));
	}

	const directCandidates = instrument.useInteractions.filter(
		(interaction) => interaction.targetItemId === action.targetItemId,
	);

	/*
	 * R33 (D113; Calder's §1): `use` means "apply these two things to each
	 * other", and applying a card to a reader is the same act as applying a
	 * reader to a card — so when the direct lookup finds nothing on A, try
	 * B's interactions with A as the target BEFORE refusing. Fires only after
	 * the direct miss, so no existing behavior moves; the alternative — a
	 * mirrored `useInteractions` block on the other item — is the same fact
	 * stated twice, going stale independently, repeated for every pair every
	 * pack ever authors. The swap also swaps the ROLES below (and the
	 * `use:` context stamp), so one act keys one way whichever order the
	 * player named it — the voice index hears a single act.
	 */
	const swappedCandidates =
		directCandidates.length === 0
			? requireItem(bundle, action.targetItemId).useInteractions.filter(
				(interaction) => interaction.targetItemId === action.instrumentItemId,
			)
			: [];
	const isSwapped = directCandidates.length === 0 && swappedCandidates.length > 0;

	if (directCandidates.length === 0 && !isSwapped) {
		return rejected(bundle, state,
			{ instrumentItemId: action.instrumentItemId, targetItemId: action.targetItemId, type: 'no_matching_interaction' },
			[speakSlot(bundle, state, 'noMatchingInteraction')],
		);
	}

	return performUse(
		bundle,
		state,
		cursor,
		isSwapped
			? { instrumentItemId: action.targetItemId, targetItemId: action.instrumentItemId }
			: { instrumentItemId: action.instrumentItemId, targetItemId: action.targetItemId },
		isSwapped ? swappedCandidates : directCandidates,
		// D269: the thing the player did not name is named first — the one they carry, whichever way the arm is written.
		action.isInstrumentImplied === true ? [speakSlot(bundle, state, 'impliedInstrument', { itemName: instrument.name })] : [],
	);
};

/**
 * The act once its arms are found: the first whose conditions all hold
 * speaks, stamped `use:<instrument>>` its target (or `use:<instrument>` alone,
 * D245), and then its effects apply in order.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param cursor - The turn's transcript cursor
 * @param roles - The instrument and, unless it was used on its own, the target — as the arms see them
 * @param candidates - The arms aimed where the player aimed, in authored order
 * @param leading - D269: what is said before the arm's own lines — the implied instrument, named
 * @returns The arm's narration and effects, or the `condition_unmet` refusal
 */
const performUse = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	cursor: TranscriptCursor,
	roles: { readonly instrumentItemId: ItemId; readonly targetItemId?: ItemId },
	candidates: readonly UseInteraction[],
	leading: readonly SpokenResponse[] = [],
): ActionHandlerResult => {
	const interaction = candidates.find((candidate) =>
		candidate.conditions.every((condition) => evaluateCondition(condition, bundle, state)),
	);

	if (interaction === undefined) {
		return rejected(bundle, state, { ...roles, type: 'condition_unmet' }, [speakSlot(bundle, state, 'conditionUnmet')]);
	}

	const narrated = completedSpeaking(
		bundle,
		state,
		cursor,
		[...leading, ...interaction.narratives],
		roles.targetItemId === undefined ? `use:${roles.instrumentItemId}` : `use:${roles.instrumentItemId}>${roles.targetItemId}`,
	);

	return withEffects(bundle, narrated, interaction.effects);
};

/**
 * The lock STILL holding this exit shut, or `undefined` when it opens: no
 * lock, nothing in the way, and the bolt either force-unlocked
 * (`unlockedExitIds` wins over the bolt) or a `lockedWhen` that reads FALSE.
 * TRUE means LOCKED, and an absent `lockedWhen` means locked until something
 * unlocks it.
 *
 * @remarks
 * D158 made `lockedWhen` a condition. The two primitives that decide this are
 * D135's event/state pair and both stay: `unlockedExitIds` is the LEDGER —
 * the bolt was cut, permanently, and history outranks the present — while
 * `lockedWhen` is a live read of how the world is now. An author picks the
 * one they mean, and a way that should stay open once opened wants the
 * ledger.
 *
 * D233 gave the lock a second hold, `blockedWhen` — the OBSTRUCTION, which
 * the ledger never clears: a guard in the doorway (an actor's `blocksExits`
 * desugars into exactly this) stands there whether or not the bolt behind
 * him was ever cut. A lock with only an obstruction has no bolt.
 *
 * Returns the LOCK rather than a boolean so the caller's narrowing is
 * type-visible. A `boolean` answer stranded the lock behind a second
 * `exit.lock === undefined` check that no test could ever enter — TypeScript
 * cannot carry a property narrowing across a call boundary, so the truth
 * "locked implies a lock exists" had to be re-asserted at runtime. Handing
 * back the lock itself states that truth in the type, and the phantom branch
 * stops existing (engineering_standards §14.1).
 */
const engagedExitLock = (
	exit: ExitDefinition,
	bundle: EngineGameBundle,
	state: RuntimeState,
): ExitLock | undefined => {
	const { lock } = exit;

	if (lock === undefined) {
		return undefined;
	}

	// D233: the OBSTRUCTION holds whatever the ledger says — a body in the
	// doorway is not undone by the bolt having been cut. Judged first, because
	// an unlocked door with a guard in it is still shut.
	if (lock.blockedWhen !== undefined && evaluateCondition(lock.blockedWhen, bundle, state)) {
		return lock;
	}

	if (state.world.unlockedExitIds.includes(exit.exitId)) {
		return undefined;
	}

	// The BOLT: absent `lockedWhen` means shut until unlocked — unless the lock
	// declares only an obstruction, in which case it has no bolt at all and
	// opens the moment the way is clear (D233).
	if (lock.lockedWhen === undefined) {
		return lock.blockedWhen === undefined ? lock : undefined;
	}

	return evaluateCondition(lock.lockedWhen, bundle, state) ? lock : undefined;
};

/**
 * What the lock says, this time (D158).
 *
 * @remarks
 * The plain-array arm is the common case and every lock the pack shipped
 * before this. The variant arm is what retired `keyItemId` and
 * `lockedWithKeyItemNarratives`: "you are carrying the answer" was one
 * `has_item` gate spelled as a dedicated field pair, and the general shape
 * says it — and every gate that pair could not — as an ordinary condition.
 */
const selectLockNarratives = (lock: ExitLock, bundle: EngineGameBundle, state: RuntimeState): readonly NarrativeLine[] => {
	const { refusal } = lock;

	// `'variants' in x` rather than `Array.isArray` — the repo's narrowing
	// idiom for these unions (`resolveRoomAmbience`, `resolvePacing`), and the
	// one TypeScript carries across a READONLY array arm.
	return 'variants' in refusal
		? (firstMatchingVariant(refusal.variants, (variant) => variant.when, bundle, state)?.narratives ?? refusal.fallback)
		: refusal;
};

